/**
 * Small, non-interactive hints layered over whatever the TV is showing
 * (TV_REVIVAL_PLAN.md §2.3 and §2.5), plus the countdown ring that
 * auto-advancing cards draw (Part 2 mode map).
 */
import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { TvNotice, TvSnapshot } from './protocol';
import { countdownProgress, gatePill, noticeGlyph, noticeTone, splitPhoneEmphasis, type TvNoticeGlyph } from './hudModel';
import { FORGET_TV_COPY } from './forgetTv';
import { appShell, isShellBackKey } from './appShell';

/** Footer for a `touch` sleep state: the player must pick up the phone. */
export function SleepFooter({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <div className="sleep-footer" role="status">
      {text}
    </div>
  );
}

/** Stage one of the lost-phone escalation: after 100 s of silence, over the
 * last frame. The session stays open and the page keeps waiting; a locked
 * phone resumes the instant it publishes again. */
export function WaitingForPhone({ reconnecting, onForget, ownerHandle = null }: { reconnecting: boolean; onForget?: (() => void) | null; ownerHandle?: string | null }) {
  return (
    <div className="link-lost-overlay">
      <div role="alert" className="link-lost-message">
        <p className="kicker">{reconnecting ? 'RECONNECTING' : 'WAITING FOR YOUR DEVICE'}</p>
        <p className="instruction">
          {reconnecting
            ? 'This TV is reconnecting to LagKing.'
            : <>Open <strong>LagKing</strong> on your device to continue here.</>}
        </p>
        <p className="passive">
          {reconnecting
            ? 'The game returns as soon as the link is back.'
            : 'Nothing to do on the TV. It picks up where you left off.'}
        </p>
        <LinkedAccount handle={ownerHandle} />
      </div>
      {onForget ? <ForgetTvControl onForget={onForget} /> : null}
    </div>
  );
}

/** "Linked to @Cole": which account this TV belongs to (Cole, 2026-09-30),
 *  so a TV paired to the wrong account says so beside the way out. */
export function LinkedAccount({ handle }: { handle: string | null | undefined }) {
  if (!handle) return null;
  return <p className="linked-account">Linked to <strong>@{handle}</strong></p>;
}

const ARROW_KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

/**
 * "Pair with a different account" (forgetTv.ts): a small secondary button
 * under the waiting message, then an in-page confirmation so a stray click
 * never unpairs. Computer: click, or Tab and Enter. TV remotes inside the
 * Samsung and LG shells: the button takes focus when it appears (there is
 * no pointer), the arrows move between buttons, OK (Enter) presses, and
 * Back cancels the confirmation instead of leaving the app.
 */
export function ForgetTvControl({ onForget }: { onForget: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const reopenFocusRef = useRef(false);

  useEffect(() => {
    if (confirming) {
      cancelRef.current?.focus();
    } else if (appShell || reopenFocusRef.current) {
      // A remote has no pointer: put focus on the only control on screen.
      triggerRef.current?.focus();
    }
    reopenFocusRef.current = false;
  }, [confirming]);

  // Inside a TV shell a remote press that lands with nothing focused (the
  // Magic Remote pointer can blur the page) brings focus back here.
  useEffect(() => {
    if (!appShell) return;
    const onKey = (event: KeyboardEvent) => {
      const root = rootRef.current;
      if (!root || root.contains(document.activeElement)) return;
      if (!ARROW_KEYS.has(event.key) && event.key !== 'Enter') return;
      event.preventDefault();
      (root.querySelector('button') as HTMLButtonElement | null)?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const cancel = () => {
    reopenFocusRef.current = true;
    setConfirming(false);
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (confirming && (event.key === 'Escape' || isShellBackKey(event.nativeEvent))) {
      // Back cancels the question; it does not leave the app.
      event.preventDefault();
      event.stopPropagation();
      cancel();
      return;
    }
    if (!ARROW_KEYS.has(event.key)) return;
    const buttons = Array.from(rootRef.current?.querySelectorAll('button') ?? []);
    if (buttons.length < 2) return;
    event.preventDefault();
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const step = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
    buttons[(index + step + buttons.length) % buttons.length]?.focus();
  };

  return (
    <div className="forget-tv" ref={rootRef} onKeyDown={onKeyDown}>
      {confirming ? (
        <div className="forget-tv-confirm" role="alertdialog" aria-labelledby="forget-tv-title" aria-describedby="forget-tv-body">
          <p id="forget-tv-title" className="forget-tv-title">{FORGET_TV_COPY.title}</p>
          <p id="forget-tv-body" className="forget-tv-body">{FORGET_TV_COPY.body}</p>
          <div className="forget-tv-buttons">
            <button type="button" ref={cancelRef} className="forget-tv-button" onClick={cancel}>
              {FORGET_TV_COPY.cancel}
            </button>
            <button type="button" className="forget-tv-button forget-tv-primary" onClick={onForget}>
              {FORGET_TV_COPY.confirm}
            </button>
          </div>
        </div>
      ) : (
        <button type="button" ref={triggerRef} className="forget-tv-trigger" onClick={() => setConfirming(true)}>
          {FORGET_TV_COPY.action}
        </button>
      )}
    </div>
  );
}

const RING_RADIUS = 44;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/**
 * A ring that empties at `endsAtMs`, with the seconds left in the middle and
 * the phone's label under it ("Next hole", "Running again"). The ring's full
 * length is the time between first sight of this end time and the end time
 * itself; clock skew is clamped in `countdownProgress`. Display only: the
 * phone advances the state, the TV only shows when.
 */
export function CountdownRing({
  endsAtMs,
  label,
  size = 'card',
}: {
  endsAtMs: number;
  label?: string | null;
  size?: 'card' | 'corner';
}) {
  const firstSeenRef = useRef<{ endsAtMs: number; atMs: number }>({ endsAtMs, atMs: Date.now() });
  if (firstSeenRef.current.endsAtMs !== endsAtMs) {
    firstSeenRef.current = { endsAtMs, atMs: Date.now() };
  }
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    setNow(Date.now());
    let frame = 0;
    const tick = () => {
      setNow(Date.now());
      if (Date.now() < endsAtMs) frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [endsAtMs]);

  if (!Number.isFinite(endsAtMs)) return null;
  const progress = countdownProgress(endsAtMs, firstSeenRef.current.atMs, now);
  const offset = RING_CIRCUMFERENCE * (1 - progress.fraction);

  return (
    <div
      className={`countdown-ring countdown-ring-${size}${progress.done ? ' done' : ''}`}
      role="timer"
      aria-live="off"
      aria-label={label ? `${label}: ${progress.seconds} seconds` : `${progress.seconds} seconds`}
    >
      <svg viewBox="0 0 100 100" aria-hidden="true">
        <circle className="countdown-track" cx="50" cy="50" r={RING_RADIUS} />
        <circle
          className="countdown-fill"
          cx="50"
          cy="50"
          r={RING_RADIUS}
          strokeDasharray={RING_CIRCUMFERENCE}
          strokeDashoffset={offset}
        />
      </svg>
      <strong>{progress.seconds}</strong>
      {label ? <span>{label}</span> : null}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Interrupts (Part 2, "Interrupts, on every state").
 * ------------------------------------------------------------------------ */

function BatteryGlyph() {
  return (
    <svg className="glyph-battery" viewBox="0 0 24 12" aria-hidden="true">
      <rect x="0.75" y="0.75" width="19.5" height="10.5" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <rect x="21.5" y="3.5" width="2" height="5" rx="0.8" fill="currentColor" />
      <rect x="2.75" y="2.75" width="4" height="6.5" rx="1" fill="currentColor" />
    </svg>
  );
}

function PhoneGlyph() {
  return (
    <svg className="glyph-phone" viewBox="0 0 14 24" aria-hidden="true">
      <rect x="1" y="1" width="12" height="22" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <rect x="5" y="19" width="4" height="1.6" rx="0.8" fill="currentColor" />
    </svg>
  );
}

const GLYPH_TEXT: Record<Exclude<TvNoticeGlyph, 'battery'>, string> = {
  moon: '☽',
  unplugged: '⚠',
  impact: '!',
  retry: '↻',
  check: '✓',
  update: '↑',
  people: '∴',
  flag: '⚑',
  info: 'i',
};

function NoticeGlyph({ glyph }: { glyph: TvNoticeGlyph }) {
  if (glyph === 'battery') return <span className="notice-icon"><BatteryGlyph /></span>;
  return <span className="notice-icon" aria-hidden="true">{GLYPH_TEXT[glyph]}</span>;
}

/** `text` with "on your device" emphasized; when the line does not say it
 * the phrase is added so a needsPhone notice always names the device. */
function PhoneLine({ text, append = true }: { text: string; append?: boolean }) {
  const split = splitPhoneEmphasis(text);
  if (!split) {
    if (!append) return <>{text}</>;
    return <>{text} <strong className="notice-phone-words">on your device</strong></>;
  }
  return (
    <>
      {split.before}
      <strong className="notice-phone-words">{split.phrase}</strong>
      {split.after}
    </>
  );
}

/**
 * The interrupt strip: an icon by kind, the notice text, its detail under
 * it, in the notice's tone. With `needsPhone` it carries a phone glyph,
 * emphasizes "on your phone", and pulses gently. `placement` 'scene' floats
 * it at the bottom center over a course or card presentation; 'card' sets
 * it in flow under the ambient or mirror card's text. The caller has already
 * applied `noticeVisible`.
 */
export function NoticeStrip({ notice, placement }: { notice: TvNotice | null; placement: 'scene' | 'card' }) {
  if (!notice) return null;
  const tone = noticeTone(notice);
  const detail = notice.detail?.trim() || null;
  return (
    <div
      className={`notice-strip notice-strip-${placement} notice-tone-${tone}${notice.needsPhone ? ' needs-phone' : ''}`}
      role={notice.needsPhone ? 'alert' : 'status'}
      data-notice-kind={notice.kind}
    >
      <NoticeGlyph glyph={noticeGlyph(notice.kind)} />
      <div className="notice-body">
        <p className="notice-text">
          {notice.needsPhone
            ? <PhoneLine text={notice.text} append={!(detail && splitPhoneEmphasis(detail))} />
            : notice.text}
        </p>
        {detail ? (
          <p className="notice-detail">
            {notice.needsPhone && !splitPhoneEmphasis(notice.text) && splitPhoneEmphasis(detail)
              ? <PhoneLine text={detail} />
              : detail}
          </p>
        ) : null}
      </div>
      {notice.needsPhone ? <span className="notice-phone" aria-hidden="true"><PhoneGlyph /></span> : null}
    </div>
  );
}

/**
 * The gate pill, shared by the course HUD and the ambient, mirror, and card
 * screens. A gate notice on the snapshot (already filtered by
 * `noticeVisible`) overrides the plain connected flag: amber "Gate asleep",
 * red "Gate not connected", or a battery glyph with "Low battery".
 */
export function GatePill({ snapshot, className }: { snapshot: TvSnapshot | null; className: string }) {
  // Before a phone attaches the TV cannot know the gate, so it says what it
  // does know (Cole, 2026-09-25: the idle screen needs the connection status).
  const pill = snapshot ? gatePill(snapshot) : { state: 'phone', label: 'DEVICE NOT CONNECTED' };
  if (!pill) return null;
  return (
    <div className={`${className} gate-pill gate-${pill.state}`} role="status" data-gate-state={pill.state}>
      {pill.state === 'battery' ? <BatteryGlyph /> : <i aria-hidden="true" />}
      {pill.label}
    </div>
  );
}
